import { serverReason } from '@/containers/agents/server-reason.util';
import type {
  BuildBudget,
  CustomRunDetail,
  CustomRunSummary,
  SampleEstimate,
  TryFailure,
} from '@/types/custom-playbooks';

// With Try it (try_it_v1) on, stored runs predate the Try contract, so their
// label makes no approval claim. Off, the Sample and Dry run names stay.
export function testRunLabel(
  run: Pick<CustomRunSummary | CustomRunDetail, 'sampleSize' | 'testRecord'>,
  tryIt = false
) {
  const records = run.sampleSize
    ? `${run.sampleSize} record${run.sampleSize === 1 ? '' : 's'}`
    : '';
  const base = tryIt
    ? records
      ? `Private run · ${records}`
      : 'Preview'
    : records
      ? `Sample · ${records}`
      : 'Dry run';
  // W4: name the picked record, such as the watched account a signal test used.
  return run.testRecord?.label ? `${base} · ${run.testRecord.label}` : base;
}

const amount = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0;

export function validSampleEstimate(value: SampleEstimate): boolean {
  return (
    !!value &&
    // A small source runs what it has (Sal, Oct 19): 0 to 5 records, a brief 1.
    Number.isInteger(value.records) &&
    value.records >= 0 &&
    value.records <= (value.brief === true ? 1 : 5) &&
    /^[a-f0-9]{64}$/.test(value.quoteId) &&
    Number.isInteger(value.playVersion) &&
    amount(value.maxCredits) &&
    Array.isArray(value.maxCreditsByRecords) &&
    (value.records === 0 || value.maxCreditsByRecords.length > 0) &&
    value.maxCreditsByRecords.every(amount) &&
    amount(value.balance) &&
    !!value.build &&
    [
      'limitCredits',
      'spentCredits',
      'reservedCredits',
      'remainingCredits',
    ].every((key) => amount(value.build[key as keyof BuildBudget])) &&
    typeof value.build.needsUser === 'boolean' &&
    typeof value.build.changeRequired === 'boolean' &&
    Array.isArray(value.byStep) &&
    value.byStep.every(
      (step) =>
        typeof step.nodeId === 'string' &&
        typeof step.label === 'string' &&
        amount(step.maxCredits)
    )
  );
}

/** Why this estimate blocks a Try, as a short label, or null when it doesn't. */
export function tryBlockedReason(quote: SampleEstimate): string | null {
  if (quote.records === 0)
    return `No ${quote.brief ? 'brief' : 'records'} to try yet`;
  if (quote.balance < quote.maxCredits)
    return 'Insufficient organization credits';
  if (quote.build.remainingCredits < quote.maxCreditsByRecords[0])
    return 'Over the Try budget';
  if (quote.build.needsUser) return 'Needs your input';
  if (quote.build.changeRequired) return 'Change required before another Try';
  return null;
}

export function tryBlocked(quote: SampleEstimate): boolean {
  return tryBlockedReason(quote) !== null;
}

export const ESTIMATE_CHANGED = 'Quote changed. Confirm the refreshed price.';

export function sampleError(error: unknown): {
  code?: string;
  message: string;
  refused: boolean;
  build?: BuildBudget;
  failure?: TryFailure;
  continuationId?: string;
} {
  const data = (
    error as {
      data?: {
        detail?: unknown;
        error?: { details?: unknown; code?: string };
        code?: string;
      };
    }
  )?.data;
  const raw = data?.detail ?? data?.error?.details ?? data;
  const detail =
    raw && typeof raw === 'object'
      ? (raw as {
          code?: string;
          build?: BuildBudget;
          failure?: TryFailure;
          continuationId?: string;
        })
      : undefined;
  const code = detail?.code ?? data?.error?.code ?? data?.code;
  const messages: Record<string, string> = {
    estimate_changed: ESTIMATE_CHANGED,
    reload_required: 'Reload required',
    insufficient_credits: 'Insufficient organization credits',
    over_run_limit: 'Agent credit limit exceeded',
    over_window_limit: 'Agent period credit limit exceeded',
    build_budget_exceeded: 'Over the Try budget',
    try_change_required: 'Change required before another Try',
    build_needs_user: 'Needs your input',
    play_changed: 'Play changed. Try it again.',
    try_approval_required: 'Try approval required',
    try_not_approvable: 'Try not approvable',
    try_preview_changed: 'Try preview changed. Try it again.',
    try_receipts_changed: 'Try results changed. Try it again.',
    try_preview_unavailable: 'Preview check unavailable',
    try_cohort_unavailable: 'Starting records unavailable. Try it again.',
    try_continuation_start_pending: 'Play on · Continuation starting',
    live_run: 'A live run is in progress',
  };
  const status = (error as { status?: unknown })?.status;
  return {
    code,
    refused: typeof status === 'number' && status >= 400 && status < 500,
    message: serverReason(
      error,
      (code && messages[code]) ||
        'Try status unknown. Check runs before trying again.'
    ),
    build: detail?.build,
    failure: detail?.failure,
    continuationId: detail?.continuationId,
  };
}
